import { GROWTH_CHART_RESULTS, GROWTH_CHART_VITALS } from '../../../../../../../constants';
import { roundoffNumberIfDecimal } from '../../../../../../../pages/utils';
import { PatientDetailsVisits } from '../../../../../../../store/reducers/prescriptionPad';
import {
    Age,
    PatientWithLocalId,
    Tool,
    VitalsEntity,
} from '../../../../../../../store/types/prescription';
import {
    calculateAge,
    getAgeInMonthsWithoutRoundOff,
    getAgeInYearsWithoutRoundOff,
    getAgeString,
} from '../../../../../../../store/utils';
import {
    DATA_TYPE,
    FIVE_TO_EIGHTEEN_LMS_TYPE,
    GROWTH_CHART_TYPE,
    TWO_TO_FIVE_WEIGHT_TO_HEIGHT_TYPE,
    ZERO_TO_FIVE_LMS_TYPE,
    ZERO_TO_TWO_WEIGHT_TO_HEIGHT_TYPE,
} from './growthChartType';

// generic
export function getAgeDetail(gestinalAgeWeeks: number, gestinalAgeDays: number) {
    return (gestinalAgeWeeks - 22) * 7 + gestinalAgeDays;
}

function standardNormalCDF(z: number) {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = z < 0 ? -1 : 1;
    const x = Math.abs(z) / Math.sqrt(2.0);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    const cdf = 0.5 * (1.0 + sign * y);

    const roundedPercent = Math.round(cdf * 100);
    return roundedPercent;
}

export const getWeeksAndDaysFromDays = (days: string) => {
    return [Math.floor(Number(days) / 7), Number(days) % 7];
};

export const getDaysFromWeeksAndDays = (week: string, days: string) => {
    return Number(week) * 7 + Number(days);
};

export const lbsToKgs = (weightInLbs: number): number => {
    return weightInLbs * 0.453592;
};

export const cmsToMeter = (cms: number): number => {
    return cms * 0.01;
};

export const feetToMeter = (feet: number): number => {
    return feet * 0.3048;
};

export const feetToCentimeter = (feet: number): number => {
    return feet * 0.3048 * 100;
};

export const getAgeAfter22Weeks = (gestAge: string) => {
    return Number(gestAge) - 154;
};

// FANTON
export function getFantonHeadCircumferenceZIndex(
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    gestinalAgeWeeks: number,
    gestinalAgeDays: number,
    headCircumference?: number,
) {
    const P = getAgeDetail(gestinalAgeWeeks, gestinalAgeDays);
    const G = headCircumference;
    let ageBasedColumnData: DATA_TYPE = {} as DATA_TYPE;

    if (gender === 'male') {
        growthChartData?.fanton_contants?.boys?.ofc?.forEach((data) => {
            if (data.age <= P) {
                ageBasedColumnData = data;
            }
        });
    } else {
        growthChartData?.fanton_contants?.girls?.ofc?.forEach((data) => {
            if (data.age <= P) {
                ageBasedColumnData = data;
            }
        });
    }

    const X = ageBasedColumnData?.l;
    const Y = ageBasedColumnData?.m;
    const Z = ageBasedColumnData?.s;

    //  here i am assuming that G is undefined in case of empty => handle accordingly
    if (P < 11 || P > 196 || !G) {
        return undefined;
    } else {
        return ((Math.pow(G / Y, X) - 1) / (X * Z)).toFixed(2);
    }
}

export function getFantonHeightZIndex(
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    gestinalAgeWeeks: number,
    gestinalAgeDays: number,
    height?: number,
) {
    const P = getAgeDetail(gestinalAgeWeeks, gestinalAgeDays);
    const F = height;
    let ageBasedColumnData: DATA_TYPE = {} as DATA_TYPE;

    if (gender === 'male') {
        growthChartData?.fanton_contants?.boys?.height?.forEach((data) => {
            if (data.age <= P) {
                ageBasedColumnData = data;
            }
        });
    } else {
        growthChartData?.fanton_contants?.girls?.height?.forEach((data) => {
            if (data.age <= P) {
                ageBasedColumnData = data;
            }
        });
    }

    const U = ageBasedColumnData?.l;
    const V = ageBasedColumnData?.m;
    const W = ageBasedColumnData?.s;

    //  here i am assuming that F is undefined in case of empty => handle accordingly
    if (P < 11 || P > 196 || !F) {
        return undefined;
    } else {
        return ((Math.pow(F / V, U) - 1) / (U * W))?.toFixed(2);
    }
}

export function getFantonWeightZIndex(
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    gestinalAgeWeeks: number,
    gestinalAgeDays: number,
    weight?: number,
) {
    const P = getAgeDetail(gestinalAgeWeeks, gestinalAgeDays);
    const C = gestinalAgeWeeks;
    const E = weight;
    let ageBasedColumnData: DATA_TYPE = {} as DATA_TYPE;

    if (gender === 'male') {
        growthChartData?.fanton_contants?.boys?.weight?.forEach((data) => {
            if (data.age <= P) {
                ageBasedColumnData = data;
            }
        });
    } else {
        growthChartData?.fanton_contants?.girls?.weight?.forEach((data) => {
            if (data.age <= P) {
                ageBasedColumnData = data;
            }
        });
    }
    const R = ageBasedColumnData?.l;
    const S = ageBasedColumnData?.m;
    const T = ageBasedColumnData?.s;
    const AC = S * Math.pow(1 + R * T * 3, 1 / R);
    const AD = S * Math.pow(1 + R * T * 3, 1 / R) - S * Math.pow(1 + R * T * 2, 1 / R);
    const AE = S * Math.pow(1 + R * T * -3, 1 / R);
    const AF = S * Math.pow(1 + R * T * -2, 1 / R) - S * Math.pow(1 + R * T * -3, 1 / R);

    //  here i am assuming that E is undefined in case of empty => handle accordingly
    if (P < 0 || P > 196 || C > 50 || !E) {
        return undefined;
    } else if ((Math.pow(E / S, R) - 1) / (R * T) > 3) {
        return (3 + (E - AC) / AD)?.toFixed();
    } else if ((Math.pow(E / S, R) - 1) / (R * T) < -3) {
        return (-3 + (E - AE) / AF)?.toFixed();
    } else {
        return ((Math.pow(E / S, R) - 1) / (R * T)).toFixed(2);
    }
}

export function getFantonWeightPercentile(
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    gestinalAgeWeeks: number,
    gestinalAgeDays: number,
    weight?: number,
) {
    const P = getAgeDetail(gestinalAgeWeeks, gestinalAgeDays);
    const E = weight;

    if (P < 4 || P > 196 || !E) {
        return undefined;
    } else {
        return standardNormalCDF(
            Number(
                getFantonWeightZIndex(
                    growthChartData,
                    gender,
                    gestinalAgeWeeks,
                    gestinalAgeDays,
                    E,
                ),
            ),
        );
    }
}

export function getFantonHeightPercentile(
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    gestinalAgeWeeks: number,
    gestinalAgeDays: number,
    height?: number,
) {
    const P = getAgeDetail(gestinalAgeWeeks, gestinalAgeDays);
    const F = height;

    if (P < 11 || P > 196 || !F) {
        return undefined;
    } else {
        return standardNormalCDF(
            Number(
                getFantonHeightZIndex(
                    growthChartData,
                    gender,
                    gestinalAgeWeeks,
                    gestinalAgeDays,
                    F,
                ),
            ),
        );
    }
}

export function getFantonHeadCircumferencePercentile(
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    gestinalAgeWeeks: number,
    gestinalAgeDays: number,
    headCircumference?: number,
) {
    const P = getAgeDetail(gestinalAgeWeeks, gestinalAgeDays);
    const G = headCircumference;

    if (P < 11 || P > 196 || !G) {
        return undefined;
    } else {
        return standardNormalCDF(
            Number(
                getFantonHeadCircumferenceZIndex(
                    growthChartData,
                    gender,
                    gestinalAgeWeeks,
                    gestinalAgeDays,
                    G,
                ),
            ),
        );
    }
}

// 0-5
export const getZeroToFiveHeightPercentile = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    heightInCms: number,
) => {
    if (gender?.toLowerCase() === 'male') {
        const index = growthChartData?.zero_to_five_height_contants.male.labels.findIndex(
            (label) => label === Math.round(age),
        );

        if (heightInCms < growthChartData?.zero_to_five_height_contants?.male?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.zero_to_five_height_contants?.male?.p_3?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.male?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.zero_to_five_height_contants?.male?.p_15?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.male?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.zero_to_five_height_contants?.male?.p_25?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.male?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.zero_to_five_height_contants?.male?.p_50?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.male?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.zero_to_five_height_contants?.male?.p_75?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.male?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.zero_to_five_height_contants?.male?.p_85?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.male?.p_97?.[index]
        ) {
            return '85-97';
        } else if (
            growthChartData?.zero_to_five_height_contants?.male?.p_97?.[index] < heightInCms
        ) {
            return '>97';
        }
    }

    if (gender?.toLowerCase() === 'female') {
        const index = growthChartData?.zero_to_five_height_contants?.female?.labels?.findIndex(
            (label) => label === Math.round(age),
        );

        if (heightInCms < growthChartData?.zero_to_five_height_contants?.female?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.zero_to_five_height_contants?.female?.p_3?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.female?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.zero_to_five_height_contants?.female?.p_15?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.female?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.zero_to_five_height_contants?.female?.p_25?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.female?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.zero_to_five_height_contants?.female?.p_50?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.female?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.zero_to_five_height_contants?.female?.p_75?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.female?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.zero_to_five_height_contants?.female?.p_85?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.zero_to_five_height_contants?.female?.p_97?.[index]
        ) {
            return '85-97';
        } else if (
            growthChartData?.zero_to_five_height_contants?.female?.p_97?.[index] < heightInCms
        ) {
            return '>97';
        }
    }
};

export const getZeroToFiveHeightZScore = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    heightInCms: number,
) => {
    let lms_Value_By_Age: ZERO_TO_FIVE_LMS_TYPE = {} as ZERO_TO_FIVE_LMS_TYPE;

    growthChartData?.zero_to_five_lms_contants.forEach((c) => {
        if (c.age <= age) {
            lms_Value_By_Age = c;
        }
    });

    if (gender === 'male') {
        if (heightInCms && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(heightInCms) / lms_Value_By_Age.height.male.m,
                    lms_Value_By_Age.height.male.l,
                ) -
                    1) /
                (lms_Value_By_Age.height.male.l * lms_Value_By_Age.height.male.s)
            ).toFixed(2);
        }
    }

    if (gender === 'female') {
        if (heightInCms && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(heightInCms) / lms_Value_By_Age.height.female.m,
                    lms_Value_By_Age.height.female.l,
                ) -
                    1) /
                (lms_Value_By_Age.height.female.l * lms_Value_By_Age.height.female.s)
            ).toFixed(2);
        }
    }
};

export const getZeroToFiveWeightPercentile = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    weightInKgs: number,
) => {
    if (gender?.toLowerCase() === 'male') {
        const index = growthChartData?.zero_to_five_weight_contants.male.labels.findIndex(
            (label) => label === Math.round(age),
        );

        if (weightInKgs < growthChartData?.zero_to_five_weight_contants?.male?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.male?.p_3?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.male?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.male?.p_15?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.male?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.male?.p_25?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.male?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.male?.p_50?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.male?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.male?.p_75?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.male?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.male?.p_85?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.male?.p_97?.[index]
        ) {
            return '85-97';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.male?.p_97?.[index] < weightInKgs
        ) {
            return '>97';
        }
    }

    if (gender?.toLowerCase() === 'female') {
        const index = growthChartData?.zero_to_five_weight_contants?.female?.labels?.findIndex(
            (label) => label === Math.round(age),
        );

        if (weightInKgs < growthChartData?.zero_to_five_weight_contants?.female?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.female?.p_3?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.female?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.female?.p_15?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.female?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.female?.p_25?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.female?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.female?.p_50?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.female?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.female?.p_75?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.female?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.female?.p_85?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.zero_to_five_weight_contants?.female?.p_97?.[index]
        ) {
            return '85-97';
        } else if (
            growthChartData?.zero_to_five_weight_contants?.female?.p_97?.[index] < weightInKgs
        ) {
            return '>97';
        }
    }
};

export const getZeroToFiveWeightZScore = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    weightInKgs: number,
) => {
    let lms_Value_By_Age: ZERO_TO_FIVE_LMS_TYPE = {} as ZERO_TO_FIVE_LMS_TYPE;

    growthChartData?.zero_to_five_lms_contants.forEach((c) => {
        if (c.age <= age) {
            lms_Value_By_Age = c;
        }
    });

    if (gender === 'male') {
        if (weightInKgs && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(weightInKgs) / lms_Value_By_Age.weight.male.m,
                    lms_Value_By_Age.weight.male.l,
                ) -
                    1) /
                (lms_Value_By_Age.weight.male.l * lms_Value_By_Age.weight.male.s)
            ).toFixed(2);
        }
    }

    if (gender === 'female') {
        if (weightInKgs && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(weightInKgs) / lms_Value_By_Age.weight.female.m,
                    lms_Value_By_Age.weight.female.l,
                ) -
                    1) /
                (lms_Value_By_Age.weight.female.l * lms_Value_By_Age.weight.female.s)
            ).toFixed(2);
        }
    }
};

export const getZeroToFiveOFCPercentile = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    ofc: number,
) => {
    if (gender?.toLowerCase() === 'male') {
        const index = growthChartData?.zero_to_five_ofc_contants.male.labels.findIndex(
            (label) => label === Math.round(age),
        );

        if (ofc < growthChartData?.zero_to_five_ofc_contants?.male?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.male?.p_3?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.male?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.male?.p_15?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.male?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.male?.p_25?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.male?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.male?.p_50?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.male?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.male?.p_75?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.male?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.male?.p_85?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.male?.p_97?.[index]
        ) {
            return '85-97';
        } else if (growthChartData?.zero_to_five_ofc_contants?.male?.p_97?.[index] < ofc) {
            return '>97';
        }
    }

    if (gender?.toLowerCase() === 'female') {
        const index = growthChartData?.zero_to_five_ofc_contants?.female?.labels?.findIndex(
            (label) => label === Math.round(age),
        );

        if (ofc < growthChartData?.zero_to_five_ofc_contants?.female?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.female?.p_3?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.female?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.female?.p_15?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.female?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.female?.p_25?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.female?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.female?.p_50?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.female?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.female?.p_75?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.female?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.zero_to_five_ofc_contants?.female?.p_85?.[index] <= ofc &&
            ofc <= growthChartData?.zero_to_five_ofc_contants?.female?.p_97?.[index]
        ) {
            return '85-97';
        } else if (growthChartData?.zero_to_five_ofc_contants?.female?.p_97?.[index] < ofc) {
            return '>97';
        }
    }
};

export const getZeroToFiveOFCZScore = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    ofc: number,
) => {
    let lms_Value_By_Age: ZERO_TO_FIVE_LMS_TYPE = {} as ZERO_TO_FIVE_LMS_TYPE;

    growthChartData?.zero_to_five_lms_contants.forEach((c) => {
        if (c.age <= age) {
            lms_Value_By_Age = c;
        }
    });

    if (gender === 'male') {
        if (ofc && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(Number(ofc) / lms_Value_By_Age.ofc.male.m, lms_Value_By_Age.ofc.male.l) -
                    1) /
                (lms_Value_By_Age.ofc.male.l * lms_Value_By_Age.ofc.male.s)
            ).toFixed(2);
        }
    }

    if (gender === 'female') {
        if (ofc && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(ofc) / lms_Value_By_Age.ofc.female.m,
                    lms_Value_By_Age.ofc.female.l,
                ) -
                    1) /
                (lms_Value_By_Age.ofc.female.l * lms_Value_By_Age.ofc.female.s)
            ).toFixed(2);
        }
    }
};

export const getZeroToTwoWeightToHeightPercentile = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    height: number,
    weight: number,
) => {
    if (gender?.toLowerCase() === 'male') {
        const index = growthChartData?.zero_to_two_weight_to_height_contants.male.labels.findIndex(
            (label) => label === Math.round(height),
        );

        if (weight < growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_3?.[index] <= weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_15?.[index] <= weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_25?.[index] <= weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_50?.[index] <= weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_75?.[index] <= weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_85?.[index] <= weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_97?.[index]
        ) {
            return '85-97';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.male?.p_97?.[index] < weight
        ) {
            return '>97';
        }
    }

    if (gender?.toLowerCase() === 'female') {
        const index = growthChartData?.zero_to_two_weight_to_height_contants?.female?.labels?.findIndex(
            (label) => label === Math.round(height),
        );

        if (weight < growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_3?.[index] <=
                weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_15?.[index] <=
                weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_25?.[index] <=
                weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_50?.[index] <=
                weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_75?.[index] <=
                weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_85?.[index] <=
                weight &&
            weight <= growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_97?.[index]
        ) {
            return '85-97';
        } else if (
            growthChartData?.zero_to_two_weight_to_height_contants?.female?.p_97?.[index] < weight
        ) {
            return '>97';
        }
    }
};

export const getZeroToTwoWeightToHeightZScore = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    height: number,
    weight: number,
) => {
    let lms_Value_By_Age: ZERO_TO_TWO_WEIGHT_TO_HEIGHT_TYPE = {} as ZERO_TO_TWO_WEIGHT_TO_HEIGHT_TYPE;

    growthChartData?.zero_to_two_weigth_to_height_constants.forEach((c) => {
        if (c.height <= height) {
            lms_Value_By_Age = c;
        }
    });

    if (gender === 'male') {
        if (weight && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(weight) / lms_Value_By_Age.zeroToTwo.male.m,
                    lms_Value_By_Age.zeroToTwo.male.l,
                ) -
                    1) /
                (lms_Value_By_Age.zeroToTwo.male.l * lms_Value_By_Age.zeroToTwo.male.s)
            ).toFixed(2);
        }
    }

    if (gender === 'female') {
        if (weight && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(weight) / lms_Value_By_Age.zeroToTwo.female.m,
                    lms_Value_By_Age.zeroToTwo.female.l,
                ) -
                    1) /
                (lms_Value_By_Age.zeroToTwo.female.l * lms_Value_By_Age.zeroToTwo.female.s)
            ).toFixed(2);
        }
    }
};

export const getTwoToFiveWeightToHeightPercentile = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    height: number,
    weight: number,
) => {
    if (gender?.toLowerCase() === 'male') {
        const index = growthChartData?.two_to_five_weight_to_height_contants.male.labels.findIndex(
            (label) => label === Math.round(height),
        );

        if (weight < growthChartData?.two_to_five_weight_to_height_contants?.male?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.male?.p_3?.[index] <= weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.male?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.male?.p_15?.[index] <= weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.male?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.male?.p_25?.[index] <= weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.male?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.male?.p_50?.[index] <= weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.male?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.male?.p_75?.[index] <= weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.male?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.male?.p_85?.[index] <= weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.male?.p_97?.[index]
        ) {
            return '85-97';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.male?.p_97?.[index] < weight
        ) {
            return '>97';
        }
    }

    if (gender?.toLowerCase() === 'female') {
        const index = growthChartData?.two_to_five_weight_to_height_contants?.female?.labels?.findIndex(
            (label) => label === Math.round(height),
        );

        if (weight < growthChartData?.two_to_five_weight_to_height_contants?.female?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.female?.p_3?.[index] <=
                weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.female?.p_15?.[index]
        ) {
            return '3-15';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.female?.p_15?.[index] <=
                weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.female?.p_25?.[index]
        ) {
            return '15-25';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.female?.p_25?.[index] <=
                weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.female?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.female?.p_50?.[index] <=
                weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.female?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.female?.p_75?.[index] <=
                weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.female?.p_85?.[index]
        ) {
            return '75-85';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.female?.p_85?.[index] <=
                weight &&
            weight <= growthChartData?.two_to_five_weight_to_height_contants?.female?.p_97?.[index]
        ) {
            return '85-97';
        } else if (
            growthChartData?.two_to_five_weight_to_height_contants?.female?.p_97?.[index] < weight
        ) {
            return '>97';
        }
    }
};

export const getTwoToFiveWeightToHeightZScore = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    height: number,
    weight: number,
) => {
    let lms_Value_By_Age: TWO_TO_FIVE_WEIGHT_TO_HEIGHT_TYPE = {} as TWO_TO_FIVE_WEIGHT_TO_HEIGHT_TYPE;

    growthChartData?.two_to_five_weigth_to_height_constants.forEach((c) => {
        if (c.height <= height) {
            lms_Value_By_Age = c;
        }
    });

    if (gender === 'male') {
        if (weight && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(weight) / lms_Value_By_Age.twoToFive.male.m,
                    lms_Value_By_Age.twoToFive.male.l,
                ) -
                    1) /
                (lms_Value_By_Age.twoToFive.male.l * lms_Value_By_Age.twoToFive.male.s)
            ).toFixed(2);
        }
    }

    if (gender === 'female') {
        if (weight && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(weight) / lms_Value_By_Age.twoToFive.female.m,
                    lms_Value_By_Age.twoToFive.female.l,
                ) -
                    1) /
                (lms_Value_By_Age.twoToFive.female.l * lms_Value_By_Age.twoToFive.female.s)
            ).toFixed(2);
        }
    }
};

// 5-18
export const getFiveToEighteenHeightPercentile = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    heightInCms: number,
) => {
    if (gender?.toLowerCase() === 'male') {
        const index = growthChartData?.five_to_eighteen_height_contants.male.labels.findIndex(
            (label) => label === Math.round(age),
        );

        if (heightInCms < growthChartData?.five_to_eighteen_height_contants?.male?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.male?.p_3?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.male?.p_10?.[index]
        ) {
            return '3-10';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.male?.p_10?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.male?.p_25?.[index]
        ) {
            return '10-25';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.male?.p_25?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.male?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.male?.p_50?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.male?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.male?.p_75?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.male?.p_90?.[index]
        ) {
            return '75-90';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.male?.p_90?.[index] <= heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.male?.p_97?.[index]
        ) {
            return '90-97';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.male?.p_97?.[index] < heightInCms
        ) {
            return '>97';
        }
    }

    if (gender?.toLowerCase() === 'female') {
        const index = growthChartData?.five_to_eighteen_height_contants?.female?.labels?.findIndex(
            (label) => label === Math.round(age),
        );

        if (heightInCms < growthChartData?.five_to_eighteen_height_contants?.female?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.female?.p_3?.[index] <=
                heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.female?.p_10?.[index]
        ) {
            return '3-10';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.female?.p_10?.[index] <=
                heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.female?.p_25?.[index]
        ) {
            return '10-25';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.female?.p_25?.[index] <=
                heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.female?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.female?.p_50?.[index] <=
                heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.female?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.female?.p_75?.[index] <=
                heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.female?.p_90?.[index]
        ) {
            return '75-90';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.female?.p_90?.[index] <=
                heightInCms &&
            heightInCms <= growthChartData?.five_to_eighteen_height_contants?.female?.p_97?.[index]
        ) {
            return '90-97';
        } else if (
            growthChartData?.five_to_eighteen_height_contants?.female?.p_97?.[index] < heightInCms
        ) {
            return '>97';
        }
    }
};

export const getFiveToEighteenHeightZScore = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    heightInCms: number,
) => {
    let lms_Value_By_Age: FIVE_TO_EIGHTEEN_LMS_TYPE = {} as FIVE_TO_EIGHTEEN_LMS_TYPE;

    growthChartData?.five_to_eighteen_lms_contants.forEach((c) => {
        if (c.age <= age) {
            lms_Value_By_Age = c;
        }
    });

    if (gender === 'male') {
        if (heightInCms && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(heightInCms) / lms_Value_By_Age.height.male.m,
                    lms_Value_By_Age.height.male.l,
                ) -
                    1) /
                (lms_Value_By_Age.height.male.l * lms_Value_By_Age.height.male.s)
            ).toFixed(2);
        }
    }

    if (gender === 'female') {
        if (heightInCms && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(heightInCms) / lms_Value_By_Age.height.female.m,
                    lms_Value_By_Age.height.female.l,
                ) -
                    1) /
                (lms_Value_By_Age.height.female.l * lms_Value_By_Age.height.female.s)
            ).toFixed(2);
        }
    }
};

export const getFiveToEighteenWeightPercentile = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    weightInKgs: number,
) => {
    if (gender?.toLowerCase() === 'male') {
        const index = growthChartData?.five_to_eighteen_weight_contants.male.labels.findIndex(
            (label) => label === Math.round(age),
        );

        if (weightInKgs < growthChartData?.five_to_eighteen_weight_contants?.male?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.male?.p_3?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.male?.p_10?.[index]
        ) {
            return '3-10';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.male?.p_10?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.male?.p_25?.[index]
        ) {
            return '10-25';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.male?.p_25?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.male?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.male?.p_50?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.male?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.male?.p_75?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.male?.p_90?.[index]
        ) {
            return '75-90';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.male?.p_90?.[index] <= weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.male?.p_97?.[index]
        ) {
            return '90-97';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.male?.p_97?.[index] < weightInKgs
        ) {
            return '>97';
        }
    }

    if (gender?.toLowerCase() === 'female') {
        const index = growthChartData?.five_to_eighteen_weight_contants?.female?.labels?.findIndex(
            (label) => label === Math.round(age),
        );

        if (weightInKgs < growthChartData?.five_to_eighteen_weight_contants?.female?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.female?.p_3?.[index] <=
                weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.female?.p_10?.[index]
        ) {
            return '3-10';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.female?.p_10?.[index] <=
                weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.female?.p_25?.[index]
        ) {
            return '10-25';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.female?.p_25?.[index] <=
                weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.female?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.female?.p_50?.[index] <=
                weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.female?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.female?.p_75?.[index] <=
                weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.female?.p_90?.[index]
        ) {
            return '75-90';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.female?.p_90?.[index] <=
                weightInKgs &&
            weightInKgs <= growthChartData?.five_to_eighteen_weight_contants?.female?.p_97?.[index]
        ) {
            return '90-97';
        } else if (
            growthChartData?.five_to_eighteen_weight_contants?.female?.p_97?.[index] < weightInKgs
        ) {
            return '>97';
        }
    }
};

export const getFiveToEighteenWeightZScore = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    weightInKgs: number,
) => {
    let lms_Value_By_Age: FIVE_TO_EIGHTEEN_LMS_TYPE = {} as FIVE_TO_EIGHTEEN_LMS_TYPE;

    growthChartData?.five_to_eighteen_lms_contants.forEach((c) => {
        if (c.age <= age) {
            lms_Value_By_Age = c;
        }
    });

    if (gender === 'male') {
        if (weightInKgs && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(weightInKgs) / lms_Value_By_Age.weight.male.m,
                    lms_Value_By_Age.weight.male.l,
                ) -
                    1) /
                (lms_Value_By_Age.weight.male.l * lms_Value_By_Age.weight.male.s)
            ).toFixed(2);
        }
    }

    if (gender === 'female') {
        if (weightInKgs && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(weightInKgs) / lms_Value_By_Age.weight.female.m,
                    lms_Value_By_Age.weight.female.l,
                ) -
                    1) /
                (lms_Value_By_Age.weight.female.l * lms_Value_By_Age.weight.female.s)
            ).toFixed(2);
        }
    }
};

export const getFiveToEighteenBMIPercentile = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    bmi: number,
) => {
    if (gender?.toLowerCase() === 'male') {
        const index = growthChartData?.five_to_eighteen_bmi_contants.male.labels.findIndex(
            (label) => label === Math.round(age),
        );

        if (bmi < growthChartData?.five_to_eighteen_bmi_contants?.male?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.male?.p_3?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.male?.p_5?.[index]
        ) {
            return '3-5';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.male?.p_5?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.male?.p_10?.[index]
        ) {
            return '5-10';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.male?.p_10?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.male?.p_25?.[index]
        ) {
            return '10-25';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.male?.p_25?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.male?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.male?.p_50?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.male?.p_71?.[index]
        ) {
            return '50-71';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.male?.p_71?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.male?.p_90?.[index]
        ) {
            return '71-90';
        } else if (growthChartData?.five_to_eighteen_bmi_contants?.male?.p_90?.[index] < bmi) {
            return '>90';
        }
    }

    if (gender?.toLowerCase() === 'female') {
        const index = growthChartData?.five_to_eighteen_bmi_contants?.female?.labels?.findIndex(
            (label) => label === Math.round(age),
        );

        if (bmi < growthChartData?.five_to_eighteen_bmi_contants?.female?.p_3?.[index]) {
            return '<3';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.female?.p_3?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.female?.p_5?.[index]
        ) {
            return '3-5';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.female?.p_5?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.female?.p_10?.[index]
        ) {
            return '5-10';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.female?.p_10?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.female?.p_25?.[index]
        ) {
            return '10-25';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.female?.p_25?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.female?.p_50?.[index]
        ) {
            return '25-50';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.female?.p_50?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.female?.p_75?.[index]
        ) {
            return '50-75';
        } else if (
            growthChartData?.five_to_eighteen_bmi_contants?.female?.p_75?.[index] <= bmi &&
            bmi <= growthChartData?.five_to_eighteen_bmi_contants?.female?.p_95?.[index]
        ) {
            return '75-95';
        } else if (growthChartData?.five_to_eighteen_bmi_contants?.female?.p_95?.[index] < bmi) {
            return '>95';
        }
    }
};

export const getFiveToEighteenBMIZScore = (
    growthChartData: GROWTH_CHART_TYPE,
    gender: 'male' | 'female',
    age: number,
    bmi: number,
) => {
    let lms_Value_By_Age: FIVE_TO_EIGHTEEN_LMS_TYPE = {} as FIVE_TO_EIGHTEEN_LMS_TYPE;

    growthChartData?.five_to_eighteen_lms_contants.forEach((c) => {
        if (c.age <= age) {
            lms_Value_By_Age = c;
        }
    });

    if (gender === 'male') {
        if (bmi && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(Number(bmi) / lms_Value_By_Age.bmi.male.m, lms_Value_By_Age.bmi.male.l) -
                    1) /
                (lms_Value_By_Age.bmi.male.l * lms_Value_By_Age.bmi.male.s)
            ).toFixed(2);
        }
    }

    if (gender === 'female') {
        if (bmi && Object.keys(lms_Value_By_Age).length) {
            return (
                (Math.pow(
                    Number(bmi) / lms_Value_By_Age.bmi.female.m,
                    lms_Value_By_Age.bmi.female.l,
                ) -
                    1) /
                (lms_Value_By_Age.bmi.female.l * lms_Value_By_Age.bmi.female.s)
            ).toFixed(2);
        }
    }
};

// Custom Tooltip
const getOrCreateTooltip = (chart: any) => {
    let tooltipEl = chart.canvas.parentNode.querySelector('div');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.style.borderRadius = '8px';
        tooltipEl.style.color = 'white';
        tooltipEl.style.opacity = 1;
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.transform = 'translate(-50%, 0)';
        tooltipEl.style.transition = 'all .1s ease';
        tooltipEl.style.background = '#FFF';
        tooltipEl.style.border = '1px solid #E8E8E8';
        tooltipEl.style['boxShadow'] = '0px 0px 8px 0px #0000000D';
        tooltipEl.style.zIndex = 9999;

        const table = document.createElement('table');
        table.style.margin = '0px';

        tooltipEl.appendChild(table);
        chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
};

export const externalTooltipHandler = (
    context: any,
    xAxisLabel: string,
    xAxisValue: string,
    yAxisLabel: string,
    yAxisValue: string,
    percentile?: string | number,
    zIndex?: string | number,
    date?: string,
) => {
    if (
        context?.tooltip?.dataPoints?.[0]?.datasetIndex + 1 !==
        context?.tooltip?.dataPoints?.[0]?.chart?.$datalabels?._datasets?.length
    ) {
        return null;
    }
    // Tooltip Element
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
    }

    // Set Text
    if (tooltip.body) {
        const parentDiv = document.createElement('div');
        parentDiv.innerHTML = `<div style="padding: 16px 20px; color: #1A1A1A; z-index: 999;">
                                    <p style="color: #484848; font-size: 12px; font-weight: 700; margin-bottom: 4px;">Visit Date: ${date}</p>
                                    <p style="color: #19A66A; font-size: 16px; font-weight: 700;">${percentile} %ile</p>
                                    <span>
                                        <span style="color: #1A1A1A; font-size: 14px; font-weight: 400;">Z-score:</span>
                                        <span style="color: #19A66A; font-size: 14px; font-weight: 700;">${
                                            zIndex || '-'
                                        }</span>
                                    </span>
                                
                                    <div style="width: 100%; height: 1px; margin: 8px 0px; background-color: #E8E8E8;"></div>
                                
                                    <div style="white-space: nowrap;">
                                        <p style="font-size: 13px; display: flex; align-items: center; justify-content: space-between;">
                                            <span style="padding-right: 50px;">${xAxisLabel}</span>
                                            <span style="font-weight: 700;">${xAxisValue}</span>
                                        </p>
                                        <p style="font-size: 13px; display: flex; align-items: center; justify-content: space-between;">
                                            <span style="padding-right: 50px;">${yAxisLabel}</span>
                                            <span style="font-weight: 700;">${yAxisValue}</span>
                                        </p>
                                    </div>
                                </div>
                                `;

        const tableRoot = tooltipEl.querySelector('table');

        // Remove old children
        while (tableRoot?.firstChild) {
            tableRoot.firstChild.remove();
        }

        // Add new children
        tableRoot.appendChild(parentDiv);
    }

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    // Calculate the position of the tooltip
    let tooltipLeft = positionX + tooltip.caretX;
    let tooltipTop = positionY + tooltip.caretY;

    // Adjust tooltip direction based on cursor position
    if (tooltipLeft < tooltipEl.offsetWidth) {
        tooltipLeft = tooltipLeft + tooltipEl.offsetWidth / 2 + 20;
        tooltipTop -= tooltipEl.offsetHeight / 2;
    } else {
        tooltipLeft = tooltipLeft - tooltipEl.offsetWidth / 2 - 20;
        tooltipTop -= tooltipEl.offsetHeight / 2;
    }

    // Set the position of the tooltip
    tooltipEl.style.left = tooltipLeft + 'px';
    tooltipEl.style.top = tooltipTop + 'px';
    tooltipEl.style.opacity = 1;
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = '0';
};

export const getGrowthChartZindexAndPercentileVitals = (
    growthChartData: GROWTH_CHART_TYPE,
    vitals: VitalsEntity[],
    gender: 'male' | 'female',
    age?: Age,
): VitalsEntity[] => {
    if (
        (!age?.dob && !age?.month) ||
        (gender?.toLowerCase() !== 'male' && gender?.toLowerCase() !== 'female')
    ) {
        return [];
    }

    const height = vitals.find((v) => v.id === 'lb-7561569471');
    const weight = vitals.find((v) => v.id === 'lb-7991006736');
    const bmi = vitals.find((v) => v.id === 'lb-5305097546');
    const ofc = vitals.find((v) => v.id === 'lb-7205446246');
    const gestationalAge = vitals.find((v) => v.id === 'lb-5257276261');
    const ageInNumber = getAgeInYearsWithoutRoundOff(age);

    const finalResult: VitalsEntity[] = [] as VitalsEntity[];

    // Body Height
    if (height) {
        const heightZIndexVital = GROWTH_CHART_RESULTS['lb-489488198'];
        const heightPercentileVital = GROWTH_CHART_RESULTS['lb-8961902374'];

        let zScore = undefined;
        let percentile = undefined;

        if (gestationalAge) {
            const heightInCms = Number(
                height?.value.code_id === 'vu-3019850218'
                    ? feetToCentimeter(parseFloat(height.value.qt))
                    : height?.value?.qt,
            );

            if (gender?.toLowerCase() === 'male') {
                // zscore and percentile
                const gAge = getWeeksAndDaysFromDays(gestationalAge?.value?.qt || '');
                zScore = heightInCms
                    ? getFantonHeightZIndex(
                          growthChartData,
                          'male',
                          gAge?.[0],
                          gAge?.[1],
                          Number(heightInCms),
                      )
                    : undefined;
                percentile = heightInCms
                    ? getFantonHeightPercentile(
                          growthChartData,
                          'male',
                          gAge?.[0],
                          gAge?.[1],
                          Number(heightInCms),
                      )
                    : undefined;
            }

            if (gender?.toLowerCase() === 'female') {
                // zscore and percentile
                const gAge = getWeeksAndDaysFromDays(gestationalAge?.value?.qt || '');
                zScore = heightInCms
                    ? getFantonHeightZIndex(
                          growthChartData,
                          'female',
                          gAge?.[0],
                          gAge?.[1],
                          Number(heightInCms),
                      )
                    : undefined;
                percentile = heightInCms
                    ? getFantonHeightPercentile(
                          growthChartData,
                          'female',
                          gAge?.[0],
                          gAge?.[1],
                          Number(heightInCms),
                      )
                    : undefined;
            }
        } else if (ageInNumber <= 5) {
            const agePointPosition = getAgeInMonthsWithoutRoundOff(age);

            const heightInCms = Number(
                height?.value.code_id === 'vu-3019850218'
                    ? feetToCentimeter(parseFloat(height.value.qt))
                    : height?.value?.qt,
            );

            if (gender?.toLowerCase() === 'male') {
                zScore = getZeroToFiveHeightZScore(
                    growthChartData,
                    'male',
                    agePointPosition,
                    heightInCms,
                );
                percentile = getZeroToFiveHeightPercentile(
                    growthChartData,
                    'male',
                    agePointPosition,
                    heightInCms,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zScore = getZeroToFiveHeightZScore(
                    growthChartData,
                    'female',
                    agePointPosition,
                    heightInCms,
                );
                percentile = getZeroToFiveHeightPercentile(
                    growthChartData,
                    'female',
                    agePointPosition,
                    heightInCms,
                );
            }
        } else if (5 < ageInNumber && ageInNumber <= 18) {
            const agePointPosition = getAgeInYearsWithoutRoundOff(age);

            const heightInCms = Number(
                height?.value.code_id === 'vu-3019850218'
                    ? feetToCentimeter(parseFloat(height.value.qt))
                    : height?.value?.qt,
            );

            if (gender?.toLowerCase() === 'male') {
                zScore = getFiveToEighteenHeightZScore(
                    growthChartData,
                    'male',
                    agePointPosition,
                    heightInCms,
                );
                percentile = getFiveToEighteenHeightPercentile(
                    growthChartData,
                    'male',
                    agePointPosition,
                    heightInCms,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zScore = getFiveToEighteenHeightZScore(
                    growthChartData,
                    'female',
                    agePointPosition,
                    heightInCms,
                );
                percentile = getFiveToEighteenHeightPercentile(
                    growthChartData,
                    'female',
                    agePointPosition,
                    heightInCms,
                );
            }
        }

        if (!isNaN(Number(zScore)) && zScore !== undefined && percentile !== undefined) {
            finalResult.push({
                date: new Date().toISOString(),
                dis_name: heightZIndexVital.dis_name,
                id: heightZIndexVital.id,
                name: heightZIndexVital.name,
                value: {
                    qt: zScore?.toString(),
                    unit: heightZIndexVital.units_with_ranges[0].dis_unit,
                    code_id: heightZIndexVital.units_with_ranges[0].code_id,
                },
            });

            finalResult.push({
                date: new Date().toISOString(),
                dis_name: heightPercentileVital.dis_name,
                id: heightPercentileVital.id,
                name: heightPercentileVital.name,
                value: {
                    qt: percentile?.toString(),
                    unit: heightPercentileVital.units_with_ranges[0].dis_unit,
                    code_id: heightPercentileVital.units_with_ranges[0].code_id,
                },
            });
        }
    }

    // Body Weight
    if (weight) {
        const weightZIndexVital = GROWTH_CHART_RESULTS['lb-1297034741'];
        const weightPercentileVital = GROWTH_CHART_RESULTS['lb-6580967293'];

        let zScore = undefined;
        let percentile = undefined;

        if (gestationalAge) {
            const weightInGrams =
                Number(
                    weight?.value.code_id === 'vu-2307875107'
                        ? lbsToKgs(parseFloat(weight.value.qt))
                        : weight?.value?.qt,
                ) * 1000;

            if (gender?.toLowerCase() === 'male') {
                const gAge = getWeeksAndDaysFromDays(gestationalAge?.value?.qt || '');
                zScore = weightInGrams
                    ? getFantonWeightZIndex(
                          growthChartData,
                          'male',
                          gAge?.[0],
                          gAge?.[1],
                          Number(weightInGrams),
                      )
                    : undefined;
                percentile = weightInGrams
                    ? getFantonWeightPercentile(
                          growthChartData,
                          'male',
                          gAge?.[0],
                          gAge?.[1],
                          Number(weightInGrams),
                      )
                    : undefined;
            }

            if (gender?.toLowerCase() === 'female') {
                const gAge = getWeeksAndDaysFromDays(gestationalAge?.value?.qt || '');
                zScore = weightInGrams
                    ? getFantonWeightZIndex(
                          growthChartData,
                          'female',
                          gAge?.[0],
                          gAge?.[1],
                          Number(weightInGrams),
                      )
                    : undefined;
                percentile = weightInGrams
                    ? getFantonWeightPercentile(
                          growthChartData,
                          'female',
                          gAge?.[0],
                          gAge?.[1],
                          Number(weightInGrams),
                      )
                    : undefined;
            }
        } else if (ageInNumber <= 5) {
            const agePointPosition = getAgeInMonthsWithoutRoundOff(age);

            const weightInKgs = Number(
                weight?.value.code_id === 'vu-2307875107'
                    ? lbsToKgs(parseFloat(weight.value.qt))
                    : weight?.value?.qt,
            );

            if (gender?.toLowerCase() === 'male') {
                zScore = getZeroToFiveWeightZScore(
                    growthChartData,
                    'male',
                    agePointPosition,
                    weightInKgs,
                );
                percentile = getZeroToFiveWeightPercentile(
                    growthChartData,
                    'male',
                    agePointPosition,
                    weightInKgs,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zScore = getZeroToFiveWeightZScore(
                    growthChartData,
                    'female',
                    agePointPosition,
                    weightInKgs,
                );
                percentile = getZeroToFiveWeightPercentile(
                    growthChartData,
                    'female',
                    agePointPosition,
                    weightInKgs,
                );
            }
        } else if (5 < ageInNumber && ageInNumber <= 18) {
            const agePointPosition = getAgeInYearsWithoutRoundOff(age);

            const weightInKgs = Number(
                weight?.value.code_id === 'vu-2307875107'
                    ? lbsToKgs(parseFloat(weight.value.qt))
                    : weight?.value?.qt,
            );

            if (gender?.toLowerCase() === 'male') {
                zScore = getFiveToEighteenWeightZScore(
                    growthChartData,
                    'male',
                    agePointPosition,
                    weightInKgs,
                );
                percentile = getFiveToEighteenWeightPercentile(
                    growthChartData,
                    'male',
                    agePointPosition,
                    weightInKgs,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zScore = getFiveToEighteenWeightZScore(
                    growthChartData,
                    'female',
                    agePointPosition,
                    weightInKgs,
                );
                percentile = getFiveToEighteenWeightPercentile(
                    growthChartData,
                    'male',
                    agePointPosition,
                    weightInKgs,
                );
            }
        }

        if (!isNaN(Number(zScore)) && zScore !== undefined && percentile !== undefined) {
            finalResult.push({
                date: new Date().toISOString(),
                dis_name: weightZIndexVital.dis_name,
                id: weightZIndexVital.id,
                name: weightZIndexVital.name,
                value: {
                    qt: zScore?.toString(),
                    unit: weightZIndexVital.units_with_ranges[0].dis_unit,
                    code_id: weightZIndexVital.units_with_ranges[0].code_id,
                },
            });
            finalResult.push({
                date: new Date().toISOString(),
                dis_name: weightPercentileVital.dis_name,
                id: weightPercentileVital.id,
                name: weightPercentileVital.name,
                value: {
                    qt: percentile?.toString(),
                    unit: weightPercentileVital.units_with_ranges[0].dis_unit,
                    code_id: weightPercentileVital.units_with_ranges[0].code_id,
                },
            });
        }
    }

    if (height && weight) {
        const heightWeightZIndexVital = GROWTH_CHART_RESULTS['lb-109574533'];
        const heightWeightPercentileVital = GROWTH_CHART_RESULTS['lb-953907228'];

        const heightInCms = Number(
            height?.value.code_id === 'vu-3019850218'
                ? feetToCentimeter(parseFloat(height.value.qt))
                : height?.value?.qt,
        );

        const weightInKgs = Number(
            weight?.value.code_id === 'vu-2307875107'
                ? lbsToKgs(parseFloat(weight.value.qt))
                : weight?.value?.qt,
        );

        let zScore = undefined;
        let percentile = undefined;

        if (gestationalAge) {
            zScore = undefined;
            percentile = undefined;
        } else if (ageInNumber <= 2) {
            if (gender?.toLowerCase() === 'male') {
                zScore = getZeroToTwoWeightToHeightZScore(
                    growthChartData,
                    'male',
                    heightInCms,
                    weightInKgs,
                );
                percentile = getZeroToTwoWeightToHeightPercentile(
                    growthChartData,
                    'male',
                    heightInCms,
                    weightInKgs,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zScore = getZeroToTwoWeightToHeightZScore(
                    growthChartData,
                    'female',
                    heightInCms,
                    weightInKgs,
                );
                percentile = getZeroToTwoWeightToHeightPercentile(
                    growthChartData,
                    'female',
                    heightInCms,
                    weightInKgs,
                );
            }
        } else if (2 < ageInNumber && ageInNumber <= 5) {
            if (gender?.toLowerCase() === 'male') {
                zScore = getTwoToFiveWeightToHeightZScore(
                    growthChartData,
                    'male',
                    heightInCms,
                    weightInKgs,
                );
                percentile = getTwoToFiveWeightToHeightPercentile(
                    growthChartData,
                    'male',
                    heightInCms,
                    weightInKgs,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zScore = getTwoToFiveWeightToHeightZScore(
                    growthChartData,
                    'female',
                    heightInCms,
                    weightInKgs,
                );
                percentile = getTwoToFiveWeightToHeightPercentile(
                    growthChartData,
                    'female',
                    heightInCms,
                    weightInKgs,
                );
            }
        }

        if (!isNaN(Number(zScore)) && zScore !== undefined && percentile !== undefined) {
            finalResult.push({
                date: new Date().toISOString(),
                dis_name: heightWeightZIndexVital.dis_name,
                id: heightWeightZIndexVital.id,
                name: heightWeightZIndexVital.name,
                value: {
                    qt: zScore?.toString(),
                    unit: heightWeightZIndexVital.units_with_ranges[0].dis_unit,
                    code_id: heightWeightZIndexVital.units_with_ranges[0].code_id,
                },
            });

            finalResult.push({
                date: new Date().toISOString(),
                dis_name: heightWeightPercentileVital.dis_name,
                id: heightWeightPercentileVital.id,
                name: heightWeightPercentileVital.name,
                value: {
                    qt: percentile?.toString(),
                    unit: heightWeightPercentileVital.units_with_ranges[0].dis_unit,
                    code_id: heightWeightPercentileVital.units_with_ranges[0].code_id,
                },
            });
        }
    }

    // BMI
    if (bmi) {
        const bmiZIndexVital = GROWTH_CHART_RESULTS['lb-2554319072'];
        const bmiPercentileVital = GROWTH_CHART_RESULTS['lb-2338475860'];

        let zScore = undefined;
        let percentile = undefined;

        if (gestationalAge) {
            zScore = undefined;
            percentile = undefined;
        } else if (5 < ageInNumber && ageInNumber <= 18) {
            const agePointPosition = getAgeInYearsWithoutRoundOff(age);
            const bmiIndex = Number(bmi?.value?.qt);

            if (gender?.toLowerCase() === 'male') {
                zScore = getFiveToEighteenBMIZScore(
                    growthChartData,
                    'male',
                    agePointPosition,
                    bmiIndex,
                );
                percentile = getFiveToEighteenBMIPercentile(
                    growthChartData,
                    'male',
                    agePointPosition,
                    bmiIndex,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zScore = getFiveToEighteenBMIZScore(
                    growthChartData,
                    'female',
                    agePointPosition,
                    bmiIndex,
                );
                percentile = getFiveToEighteenBMIPercentile(
                    growthChartData,
                    'female',
                    agePointPosition,
                    bmiIndex,
                );
            }
        }

        if (!isNaN(Number(zScore)) && zScore !== undefined && percentile !== undefined) {
            finalResult.push({
                date: new Date().toISOString(),
                dis_name: bmiZIndexVital.dis_name,
                id: bmiZIndexVital.id,
                name: bmiZIndexVital.name,
                value: {
                    qt: zScore?.toString(),
                    unit: bmiZIndexVital.units_with_ranges[0].dis_unit,
                    code_id: bmiZIndexVital.units_with_ranges[0].code_id,
                },
            });

            finalResult.push({
                date: new Date().toISOString(),
                dis_name: bmiPercentileVital.dis_name,
                id: bmiPercentileVital.id,
                name: bmiPercentileVital.name,
                value: {
                    qt: percentile?.toString(),
                    unit: bmiPercentileVital.units_with_ranges[0].dis_unit,
                    code_id: bmiPercentileVital.units_with_ranges[0].code_id,
                },
            });
        }
    }

    // OFC
    if (ofc) {
        const ofcZIndexVital = GROWTH_CHART_RESULTS['lb-9521575216'];
        const ofcPercentileVital = GROWTH_CHART_RESULTS['lb-1370917773'];

        let zScore = undefined;
        let percentile = undefined;

        if (gestationalAge) {
            const ofcInCms = Number(ofc?.value?.qt);

            if (gender?.toLowerCase() === 'male') {
                // zscore and percentile
                const gAge = getWeeksAndDaysFromDays(gestationalAge?.value?.qt || '');
                zScore = ofcInCms
                    ? getFantonHeadCircumferenceZIndex(
                          growthChartData,
                          'male',
                          gAge?.[0],
                          gAge?.[1],
                          Number(ofcInCms),
                      )
                    : undefined;

                percentile = ofcInCms
                    ? getFantonHeadCircumferencePercentile(
                          growthChartData,
                          'male',
                          gAge?.[0],
                          gAge?.[1],
                          Number(ofcInCms),
                      )
                    : undefined;
            }

            if (gender?.toLowerCase() === 'female') {
                // zscore and percentile
                const gAge = getWeeksAndDaysFromDays(gestationalAge?.value?.qt || '');
                zScore = ofcInCms
                    ? getFantonHeadCircumferenceZIndex(
                          growthChartData,
                          'female',
                          gAge?.[0],
                          gAge?.[1],
                          Number(ofcInCms),
                      )
                    : undefined;

                percentile = ofcInCms
                    ? getFantonHeadCircumferencePercentile(
                          growthChartData,
                          'female',
                          gAge?.[0],
                          gAge?.[1],
                          Number(ofcInCms),
                      )
                    : undefined;
            }
        } else if (ageInNumber <= 5) {
            const agePointPosition = getAgeInMonthsWithoutRoundOff(age);
            const ofcInCms = Number(ofc?.value?.qt);

            if (gender?.toLowerCase() === 'male') {
                zScore = getZeroToFiveOFCZScore(
                    growthChartData,
                    'male',
                    agePointPosition,
                    ofcInCms,
                );
                percentile = getZeroToFiveOFCPercentile(
                    growthChartData,
                    'male',
                    agePointPosition,
                    ofcInCms,
                );
            }

            if (gender?.toLowerCase() === 'female') {
                zScore = getZeroToFiveOFCZScore(
                    growthChartData,
                    'female',
                    agePointPosition,
                    ofcInCms,
                );
                percentile = getZeroToFiveOFCPercentile(
                    growthChartData,
                    'female',
                    agePointPosition,
                    ofcInCms,
                );
            }
        }

        if (!isNaN(Number(zScore)) && zScore !== undefined && percentile !== undefined) {
            finalResult.push({
                date: new Date().toISOString(),
                dis_name: ofcZIndexVital.dis_name,
                id: ofcZIndexVital.id,
                name: ofcZIndexVital.name,
                value: {
                    qt: zScore?.toString(),
                    unit: ofcZIndexVital.units_with_ranges[0].dis_unit,
                    code_id: ofcZIndexVital.units_with_ranges[0].code_id,
                },
            });

            finalResult.push({
                date: new Date().toISOString(),
                dis_name: ofcPercentileVital.dis_name,
                id: ofcPercentileVital.id,
                name: ofcPercentileVital.name,
                value: {
                    qt: percentile?.toString(),
                    unit: ofcPercentileVital.units_with_ranges[0].dis_unit,
                    code_id: ofcPercentileVital.units_with_ranges[0].code_id,
                },
            });
        }
    }

    return finalResult;
};

export const WHITELISTED_GROWTH_CHART_KEYS_BY_AGE: { [key: string]: { [key: string]: boolean } } = {
    fanton: {
        'lb-7991006736': true,
        'lb-7561569471': true,
        'lb-7205446246': true,
        'lb-5257276261': true,
    },
    zeroToFive: { 'lb-7991006736': true, 'lb-7561569471': true, 'lb-7205446246': true },
    fiveToEighteen: {
        'lb-7991006736': true,
        'lb-7561569471': true,
        'lb-5305097546': true,
    },
};

export const getWhiteListedGrowthChartVitalsByAge = (ageInNumber: number, isPreTerm?: boolean) => {
    return isPreTerm
        ? GROWTH_CHART_VITALS.filter((v) => WHITELISTED_GROWTH_CHART_KEYS_BY_AGE.fanton?.[v.id])
        : ageInNumber <= 5
        ? GROWTH_CHART_VITALS.filter((v) => WHITELISTED_GROWTH_CHART_KEYS_BY_AGE.zeroToFive?.[v.id])
        : 5 < ageInNumber && ageInNumber <= 18
        ? GROWTH_CHART_VITALS.filter(
              (v) => WHITELISTED_GROWTH_CHART_KEYS_BY_AGE.fiveToEighteen?.[v.id],
          )
        : [];
};

export const getHeightAndAgeVitalsFromVisits = (
    type: 'pre-term' | '0-5' | '5-18',
    visits?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string;
        flavour?: string;
        translateLang?: string;
        patient?: PatientWithLocalId;
    })[],
) => {
    if (type === 'pre-term') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const height = visit.vitals?.find((vital) => vital.id === 'lb-7561569471');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-489488198');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-8961902374');

                if (gestational && height && zIndex && percentile) {
                    return {
                        height: Number(
                            height?.value.code_id === 'vu-3019850218'
                                ? feetToCentimeter(parseFloat(height.value.qt))
                                : height?.value?.qt,
                        ),
                        age: getAgeAfter22Weeks(gestational?.value?.qt),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayHeight: `${roundoffNumberIfDecimal(
                            Number(
                                height?.value.code_id === 'vu-3019850218'
                                    ? feetToCentimeter(parseFloat(height.value.qt))
                                    : height?.value?.qt,
                            ),
                        )} cms`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    if (type === '0-5') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const height = visit.vitals?.find((vital) => vital.id === 'lb-7561569471');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-489488198');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-8961902374');
                const ageInNumber = getAgeInYearsWithoutRoundOff(
                    visit.patient?.profile?.personal?.age,
                    visit.date,
                );

                if (!gestational && height && zIndex && percentile && ageInNumber <= 5) {
                    return {
                        height: Number(
                            height?.value.code_id === 'vu-3019850218'
                                ? feetToCentimeter(parseFloat(height.value.qt))
                                : height?.value?.qt,
                        ),
                        age: getAgeInMonthsWithoutRoundOff(
                            visit.patient?.profile?.personal?.age,
                            visit?.date,
                        ),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayHeight: `${roundoffNumberIfDecimal(
                            Number(
                                height?.value.code_id === 'vu-3019850218'
                                    ? feetToCentimeter(parseFloat(height.value.qt))
                                    : height?.value?.qt,
                            ),
                        )} cms`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    if (type === '5-18') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const height = visit.vitals?.find((vital) => vital.id === 'lb-7561569471');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-489488198');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-8961902374');
                const ageInNumber = getAgeInYearsWithoutRoundOff(
                    visit.patient?.profile?.personal?.age,
                    visit.date,
                );

                if (
                    !gestational &&
                    height &&
                    zIndex &&
                    percentile &&
                    5 < ageInNumber &&
                    ageInNumber <= 18
                ) {
                    return {
                        height: Number(
                            height?.value.code_id === 'vu-3019850218'
                                ? feetToCentimeter(parseFloat(height.value.qt))
                                : height?.value?.qt,
                        ),
                        age: getAgeInYearsWithoutRoundOff(
                            visit.patient?.profile?.personal?.age,
                            visit?.date,
                        ),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayHeight: `${roundoffNumberIfDecimal(
                            Number(
                                height?.value.code_id === 'vu-3019850218'
                                    ? feetToCentimeter(parseFloat(height.value.qt))
                                    : height?.value?.qt,
                            ),
                        )} cms`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    return [];
};

export const getWeightAndAgeVitalsFromVisits = (
    type: 'pre-term' | '0-5' | '5-18',
    visits?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string;
        flavour?: string;
        translateLang?: string;
        patient?: PatientWithLocalId;
    })[],
) => {
    if (type === 'pre-term') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const weight = visit.vitals?.find((vital) => vital.id === 'lb-7991006736');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-1297034741');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-6580967293');

                if (gestational && weight && zIndex && percentile) {
                    return {
                        weight:
                            Number(
                                weight?.value.code_id === 'vu-2307875107'
                                    ? lbsToKgs(parseFloat(weight.value.qt))
                                    : weight?.value?.qt,
                            ) * 1000,
                        age: getAgeAfter22Weeks(gestational?.value?.qt),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayWeight: `${roundoffNumberIfDecimal(
                            Number(
                                weight?.value.code_id === 'vu-2307875107'
                                    ? lbsToKgs(parseFloat(weight.value.qt))
                                    : weight?.value?.qt,
                            ),
                        )} kgs`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    if (type === '0-5') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const weight = visit.vitals?.find((vital) => vital.id === 'lb-7991006736');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-1297034741');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-6580967293');
                const ageInNumber = getAgeInYearsWithoutRoundOff(
                    visit.patient?.profile?.personal?.age,
                    visit.date,
                );

                if (!gestational && weight && zIndex && percentile && ageInNumber <= 5) {
                    return {
                        weight: Number(
                            weight?.value.code_id === 'vu-2307875107'
                                ? lbsToKgs(parseFloat(weight.value.qt))
                                : weight?.value?.qt,
                        ),
                        age: getAgeInMonthsWithoutRoundOff(
                            visit.patient?.profile?.personal?.age,
                            visit?.date,
                        ),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayWeight: `${roundoffNumberIfDecimal(
                            Number(
                                weight?.value.code_id === 'vu-2307875107'
                                    ? lbsToKgs(parseFloat(weight.value.qt))
                                    : weight?.value?.qt,
                            ),
                        )} kgs`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    if (type === '5-18') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const weight = visit.vitals?.find((vital) => vital.id === 'lb-7991006736');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-1297034741');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-6580967293');
                const ageInNumber = getAgeInYearsWithoutRoundOff(
                    visit.patient?.profile?.personal?.age,
                    visit.date,
                );

                if (
                    !gestational &&
                    weight &&
                    zIndex &&
                    percentile &&
                    5 < ageInNumber &&
                    ageInNumber <= 18
                ) {
                    return {
                        weight: Number(
                            weight?.value.code_id === 'vu-2307875107'
                                ? lbsToKgs(parseFloat(weight.value.qt))
                                : weight?.value?.qt,
                        ),
                        age: getAgeInYearsWithoutRoundOff(
                            visit.patient?.profile?.personal?.age,
                            visit?.date,
                        ),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayWeight: `${roundoffNumberIfDecimal(
                            Number(
                                weight?.value.code_id === 'vu-2307875107'
                                    ? lbsToKgs(parseFloat(weight.value.qt))
                                    : weight?.value?.qt,
                            ),
                        )} kgs`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    return [];
};

export const getOFCAndAgeVitalsFromVisits = (
    type: 'pre-term' | '0-5',
    visits?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string;
        flavour?: string;
        translateLang?: string;
        patient?: PatientWithLocalId;
    })[],
) => {
    if (type === 'pre-term') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const ofc = visit.vitals?.find((vital) => vital.id === 'lb-7205446246');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-9521575216');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-1370917773');

                if (gestational && ofc && zIndex && percentile) {
                    return {
                        ofc: Number(ofc?.value?.qt),
                        age: getAgeAfter22Weeks(gestational?.value?.qt),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayOFC: `${roundoffNumberIfDecimal(
                            Number(
                                ofc?.value.code_id === 'vu-3019850218'
                                    ? feetToCentimeter(parseFloat(ofc.value.qt))
                                    : ofc?.value?.qt,
                            ),
                        )} cms`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    if (type === '0-5') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const ofc = visit.vitals?.find((vital) => vital.id === 'lb-7205446246');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-9521575216');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-1370917773');
                const ageInNumber = getAgeInYearsWithoutRoundOff(
                    visit.patient?.profile?.personal?.age,
                    visit.date,
                );

                if (!gestational && ofc && zIndex && percentile && ageInNumber <= 5) {
                    return {
                        ofc: Number(ofc?.value?.qt),
                        age: getAgeInMonthsWithoutRoundOff(
                            visit.patient?.profile?.personal?.age,
                            visit?.date,
                        ),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayOFC: `${roundoffNumberIfDecimal(
                            Number(
                                ofc?.value.code_id === 'vu-3019850218'
                                    ? feetToCentimeter(parseFloat(ofc.value.qt))
                                    : ofc?.value?.qt,
                            ),
                        )} cms`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    return [];
};

export const getBMIAndAgeVitalsFromVisits = (
    type: '5-18',
    visits?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string;
        flavour?: string;
        translateLang?: string;
        patient?: PatientWithLocalId;
    })[],
) => {
    if (type === '5-18') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const bmi = visit.vitals?.find((vital) => vital.id === 'lb-5305097546');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-2554319072');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-2338475860');
                const ageInNumber = getAgeInYearsWithoutRoundOff(
                    visit.patient?.profile?.personal?.age,
                    visit.date,
                );

                if (
                    !gestational &&
                    bmi &&
                    zIndex &&
                    percentile &&
                    5 < ageInNumber &&
                    ageInNumber <= 18
                ) {
                    return {
                        bmi: Number(
                            bmi?.value.code_id === 'vu-3019850218'
                                ? feetToCentimeter(parseFloat(bmi.value.qt))
                                : bmi?.value?.qt,
                        ),
                        age: getAgeInYearsWithoutRoundOff(
                            visit.patient?.profile?.personal?.age,
                            visit?.date,
                        ),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        ageString: getAgeString(
                            {
                                age: visit.patient?.profile?.personal?.age?.value,
                                month:
                                    visit.patient?.profile?.personal?.age?.month ||
                                    (visit.patient?.profile?.personal?.age as any)?.ageInMonths,
                                dob: visit.patient?.profile?.personal?.age?.dob,
                            },
                            undefined,
                            undefined,
                            visit?.date,
                        ),
                        displayBMI: roundoffNumberIfDecimal(
                            Number(
                                bmi?.value.code_id === 'vu-3019850218'
                                    ? feetToCentimeter(parseFloat(bmi.value.qt))
                                    : bmi?.value?.qt,
                            ),
                        ),
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    return [];
};

export const getWeightAndHeightVitalsFromVisits = (
    type: '0-5',
    visits?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string;
        flavour?: string;
        translateLang?: string;
        patient?: PatientWithLocalId;
    })[],
) => {
    if (type === '0-5') {
        return visits
            ?.map((visit) => {
                const gestational = visit.vitals?.find((vital) => vital.id === 'lb-5257276261');
                const weight = visit.vitals?.find((vital) => vital.id === 'lb-7991006736');
                const height = visit.vitals?.find((vital) => vital.id === 'lb-7561569471');
                const zIndex = visit.vitals?.find((vital) => vital.id === 'lb-109574533');
                const percentile = visit.vitals?.find((vital) => vital.id === 'lb-953907228');
                const ageInNumber = getAgeInYearsWithoutRoundOff(
                    visit.patient?.profile?.personal?.age,
                    visit.date,
                );

                if (!gestational && weight && height && zIndex && percentile && ageInNumber <= 5) {
                    return {
                        weight: Number(
                            weight?.value.code_id === 'vu-2307875107'
                                ? lbsToKgs(parseFloat(weight.value.qt))
                                : weight?.value?.qt,
                        ),
                        height: Number(
                            height?.value.code_id === 'vu-3019850218'
                                ? feetToCentimeter(parseFloat(height.value.qt))
                                : height?.value?.qt,
                        ),
                        zIndex: zIndex?.value.qt,
                        percentile: percentile?.value.qt,
                        visitDate: visit?.date,
                        displayWeight: `${roundoffNumberIfDecimal(
                            Number(
                                weight?.value.code_id === 'vu-2307875107'
                                    ? lbsToKgs(parseFloat(weight.value.qt))
                                    : weight?.value?.qt,
                            ),
                        )} cms`,
                        displayHeight: `${roundoffNumberIfDecimal(
                            Number(
                                height?.value.code_id === 'vu-3019850218'
                                    ? feetToCentimeter(parseFloat(height.value.qt))
                                    : height?.value?.qt,
                            ),
                        )} kgs`,
                    };
                }

                return;
            })
            .filter(Boolean);
    }

    return [];
};

export function graphIndexColor(index: number, isPrintMode?: boolean) {
    const colors = isPrintMode ? ['#000000', '#000000'] : ['#215FFF', '#215FFF'];
    return colors[index];
}

export const WEIGHT_TO_HEIGHT = 'weight-to-height';
export const WEIGHT_TO_AGE = 'weight-to-age';
export const HEIGHT_TO_AGE = 'height-to-age';
export const BMI_TO_AGE = 'bmi-to-age';
export const OFC_TO_AGE = 'ofc-to-age';
